/**
 * VariantEditor — Tier-3 Disclosure Engine M4.
 *
 * Filterable table of `DisclosureVariants` with full CRUD wired to M3:
 *   POST   /v3/admin/disclosures/variants
 *   DELETE /v3/admin/disclosures/variants/{variantKey}/{variantCode}
 *
 * Composite key is (VariantKey, VariantCode); CustomerType qualifies a row
 * but doesn't participate in the DELETE path (matches M3 backend).
 *
 * 2026-10-07: the form used free text for the template code, customer type and the
 * selector. Template and customer type are now pickers; the selector is built from
 * plan codes + "use the text of" picker in the only grammar the server parses
 * (VariantResolverService: `planCode in ('X')` + `aliasCODE`), and every row shows
 * what the package builder actually reads — including text it ignores (the seeded
 * IRA rows say "alias→CODE", which the server's regex does not match).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { ArrowLeft, Plus, RefreshCw, Search, Trash2, Edit3 } from '@retail-ops/panel-shell';

import { useDisclosuresConfig } from '@/contexts/DisclosuresContext';
import type { DisclosureVariantRow } from '@/services/disclosuresApi';
import {
  upsertVariant,
  deleteVariant,
  type DisclosureVariantUpsertInput,
} from '@/services/disclosuresAdminApi';

import { composeSelector, describeSelector, parseSelector } from './disclosureModel';
import {
  Checkbox,
  ConfirmDialog,
  Field,
  FieldGrid,
  Modal,
  NumberInput,
  Select,
  TextInput,
  Toast,
  useToast,
} from './_shared';

interface FormShape {
  variantKey: string;
  variantCode: string;
  customerType: string;
  selectorExpr: string;
  sortOrder: string;
  isActive: boolean;
}

const EMPTY_FORM: FormShape = {
  variantKey: '',
  variantCode: '',
  customerType: '',
  selectorExpr: '',
  sortOrder: '0',
  isActive: true,
};

function rowToForm(r: DisclosureVariantRow | null): FormShape {
  if (!r) return EMPTY_FORM;
  const get = (k: string): string => {
    const v = (r as Record<string, unknown>)[k];
    return v === null || v === undefined ? '' : String(v);
  };
  return {
    variantKey: get('variantKey'),
    variantCode: get('variantCode'),
    customerType: get('customerType'),
    selectorExpr: get('selectorExpr'),
    sortOrder: get('sortOrder') || '0',
    isActive: r.isActive !== false,
  };
}

function formToInput(f: FormShape): DisclosureVariantUpsertInput {
  return {
    variantKey: f.variantKey.trim(),
    variantCode: f.variantCode.trim(),
    customerType: f.customerType.trim() || null,
    selectorExpr: f.selectorExpr.trim() || null,
    sortOrder: f.sortOrder.trim() ? Number(f.sortOrder) : 0,
    isActive: f.isActive,
  };
}

export function VariantEditor(): JSX.Element {
  const { envelope, state, error, refresh, options } = useDisclosuresConfig();
  const templateCodes = useMemo(
    () => envelope.templates.map((t) => ({ code: String(t.code ?? ''), name: String(t.displayName ?? '') })).sort((a, b) => a.code.localeCompare(b.code)),
    [envelope.templates],
  );
  const [filter, setFilter] = useState<string>('');
  const [editorOpen, setEditorOpen] = useState<boolean>(false);
  const [editing, setEditing] = useState<DisclosureVariantRow | null>(null);
  const [form, setForm] = useState<FormShape>(EMPTY_FORM);
  const [busy, setBusy] = useState<boolean>(false);
  const [inlineErr, setInlineErr] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<DisclosureVariantRow | null>(null);
  const { toast, push, clear } = useToast();

  const [planText, setPlanText] = useState<string>('');
  useEffect(() => {
    if (editorOpen) {
      const f = rowToForm(editing);
      setForm(f);
      setPlanText(parseSelector(f.selectorExpr).planCodeSets.flat().join(', '));
      setInlineErr(null);
    }
  }, [editorOpen, editing]);

  const rows = useMemo<ReadonlyArray<DisclosureVariantRow>>(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return envelope.variants;
    return envelope.variants.filter((v) => {
      const haystack = [v.variantKey, v.variantCode, v.customerType, v.selectorExpr]
        .map((x) => String(x ?? '').toLowerCase())
        .join('|');
      return haystack.includes(q);
    });
  }, [envelope.variants, filter]);

  const set = <K extends keyof FormShape>(key: K, value: FormShape[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const canSave = !busy && form.variantKey.trim() !== '' && form.variantCode.trim() !== '';

  const submit = async (): Promise<void> => {
    if (!canSave) return;
    setBusy(true);
    setInlineErr(null);
    try {
      const result = await upsertVariant(formToInput(form));
      if (!result.success) {
        const msg = result.error ?? 'Save failed.';
        setInlineErr(msg);
        push('err', msg);
        return;
      }
      push('ok', `Variant ${form.variantKey} / ${form.variantCode} saved.`);
      setEditorOpen(false);
      setEditing(null);
      void refresh();
    } finally {
      setBusy(false);
    }
  };

  const onDeleteConfirm = useCallback(async (): Promise<void> => {
    if (!confirmDelete) return;
    const k = String(confirmDelete.variantKey ?? '');
    const c = String(confirmDelete.variantCode ?? '');
    if (!k || !c) {
      setConfirmDelete(null);
      return;
    }
    setBusy(true);
    try {
      const result = await deleteVariant(k, c);
      if (!result.success) {
        push('err', result.error ?? 'Delete failed.');
        return;
      }
      push('ok', `Variant ${k} / ${c} deleted.`);
      setConfirmDelete(null);
      void refresh();
    } finally {
      setBusy(false);
    }
  }, [confirmDelete, push, refresh]);

  return (
    <>
      <Toast toast={toast} onClose={clear} />

      <nav className="back-link-row" aria-label="breadcrumb">
        <Link to="/" className="back-link">Home</Link>
        <span aria-hidden="true"> / </span>
        <Link to="/disclosures" className="back-link">Disclosure Engine</Link>
        <span aria-hidden="true"> / </span>
        <span>Variants</span>
      </nav>

      <Link to="/disclosures" className="back-link">
        <ArrowLeft size={14} /> Back to Disclosure Engine
      </Link>

      <section className="page-header">
        <h1 className="page-header__title">Disclosure Variants</h1>
        <p className="page-header__subtitle page-intro">
          When an application has a matching plan (and customer type), the package builder
          swaps a template&apos;s text for another template&apos;s active text. Rows are grouped by
          family; the last column says exactly what the package builder reads from each row.
        </p>
      </section>

      <header
        className="card-header"
        style={{ display: 'flex', alignItems: 'center', gap: 12 }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Search size={14} aria-hidden="true" />
          <TextInput
            placeholder="Filter by key, code, customer type, selector…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={{ minWidth: 280, width: 280 }}
          />
        </div>
        <div className="toolbar__spacer" style={{ flex: 1 }} />
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => void refresh()}
          disabled={state === 'loading'}
        >
          <RefreshCw size={14} /> {state === 'loading' ? 'Refreshing…' : 'Refresh'}
        </button>
        <button
          type="button"
          className="btn btn--primary btn--sm"
          onClick={() => {
            setEditing(null);
            setEditorOpen(true);
          }}
        >
          <Plus size={14} /> New variant
        </button>
      </header>

      {error && <div className="banner banner--err" role="alert">{error}</div>}

      {state === 'ok' && rows.length === 0 && (
        <div className="empty">No variants match the current filter.</div>
      )}

      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="table table--striped table--hover">
            <thead>
              <tr>
                <th>Family</th>
                <th>Template</th>
                <th>Customer type</th>
                <th>What the package builder reads</th>
                <th>Sort</th>
                <th>Active</th>
                <th className="col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((v, i) => (
                <tr key={`${String(v.variantKey)}-${String(v.variantCode)}-${i}`}>
                  <td><code className="cell-id">{String(v.variantKey ?? '—')}</code></td>
                  <td><code>{String(v.variantCode ?? '—')}</code></td>
                  <td>{v.customerType ? String(v.customerType) : '—'}</td>
                  <td>
                    <span style={{ fontSize: 12 }}>{describeSelector(v.selectorExpr)}</span>
                    {v.selectorExpr ? (
                      <div><code style={{ fontSize: 11, color: 'var(--ink-500)' }}>{String(v.selectorExpr)}</code></div>
                    ) : null}
                  </td>
                  <td>{String((v as Record<string, unknown>)['sortOrder'] ?? 0)}</td>
                  <td>
                    {v.isActive === false ? (
                      <span className="pill pill--muted">Inactive</span>
                    ) : (
                      <span className="pill pill--ok">Active</span>
                    )}
                  </td>
                  <td className="col-actions" style={{ whiteSpace: 'nowrap' }}>
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      onClick={() => {
                        setEditing(v);
                        setEditorOpen(true);
                      }}
                    >
                      <Edit3 size={12} /> Edit
                    </button>{' '}
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      onClick={() => setConfirmDelete(v)}
                      style={{ color: 'var(--pcb-red)' }}
                    >
                      <Trash2 size={12} /> Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {rows.length > 0 && (
        <footer className="list-footer">
          <span>Total: {rows.length} of {envelope.variants.length}</span>
        </footer>
      )}

      <Modal
        open={editorOpen}
        onClose={() => {
          if (!busy) {
            setEditorOpen(false);
            setEditing(null);
          }
        }}
        title={editing ? `Edit variant ${form.variantKey} / ${form.variantCode}` : 'New variant'}
        width={600}
        closeOnBackdrop={!busy}
        footer={
          <>
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => {
                setEditorOpen(false);
                setEditing(null);
              }}
              disabled={busy}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => void submit()}
              disabled={!canSave}
            >
              {busy ? 'Saving…' : editing ? 'Save' : 'Create'}
            </button>
          </>
        }
      >
        {inlineErr && (
          <div className="banner banner--err" role="alert" style={{ marginBottom: 12 }}>
            {inlineErr}
          </div>
        )}
        <FieldGrid columns={2}>
          <Field label="Family" required hint="Groups the templates that can swap text (the template's Variant family).">
            <TextInput
              value={form.variantKey}
              onChange={(e) => set('variantKey', e.target.value)}
              readOnly={editing !== null}
            />
          </Field>
          <Field label="Template" required hint="The template this row belongs to.">
            <Select
              value={form.variantCode}
              onChange={(e) => set('variantCode', e.target.value)}
              disabled={editing !== null}
              aria-label="Template"
            >
              <option value="">Choose…</option>
              {templateCodes.map((t) => (
                <option key={t.code} value={t.code}>{t.code} — {t.name}</option>
              ))}
            </Select>
          </Field>
          <Field label="Customer type" hint="Empty = any customer type.">
            <Select value={form.customerType} onChange={(e) => set('customerType', e.target.value)} aria-label="Customer type">
              <option value="">Any customer type</option>
              {form.customerType && !(options?.entityTypes ?? []).some((x) => x.value === form.customerType) && (
                <option value={form.customerType}>{form.customerType} (not in the catalog)</option>
              )}
              {(options?.entityTypes ?? []).map((x) => (
                <option key={x.value} value={x.value}>{x.label || x.value}</option>
              ))}
            </Select>
          </Field>
          <Field label="Sort order">
            <NumberInput
              value={form.sortOrder}
              onChange={(e) => set('sortOrder', e.target.value)}
            />
          </Field>
          <Field label="Only for plan codes" hint="Comma-separated IRA plan codes, e.g. S, SEP. Empty = any plan.">
            <TextInput
              value={planText}
              onChange={(e) => {
                const text = e.target.value.replace(/[^A-Za-z0-9, ]/g, '');
                setPlanText(text);
                set(
                  'selectorExpr',
                  composeSelector(text.split(','), parseSelector(form.selectorExpr).aliasTarget ?? '') ?? '',
                );
              }}
            />
          </Field>
          <Field label="Use the text of" hint="When the plan matches, this template's active text is used instead." span={2}>
            <Select
              value={parseSelector(form.selectorExpr).aliasTarget ?? ''}
              onChange={(e) =>
                set('selectorExpr', composeSelector(parseSelector(form.selectorExpr).planCodeSets.flat(), e.target.value) ?? '')
              }
              aria-label="Use the text of"
            >
              <option value="">Its own text (no swap)</option>
              {templateCodes.map((t) => (
                <option key={t.code} value={t.code}>{t.code} — {t.name}</option>
              ))}
            </Select>
          </Field>
          <div style={{ gridColumn: 'span 2', fontSize: 12, marginBottom: 12 }}>
            The package builder reads: <strong>{describeSelector(form.selectorExpr)}</strong>
            {form.selectorExpr && <div><code style={{ fontSize: 11 }}>{form.selectorExpr}</code></div>}
          </div>
          <Field label="Flags" span={2}>
            <Checkbox
              label="Active"
              checked={form.isActive}
              onChange={(e) => set('isActive', e.target.checked)}
            />
          </Field>
        </FieldGrid>
      </Modal>

      <ConfirmDialog
        open={confirmDelete !== null}
        title="Delete variant"
        message={
          <span>
            Soft-delete variant{' '}
            <code>{String(confirmDelete?.variantKey ?? '')}</code> /{' '}
            <code>{String(confirmDelete?.variantCode ?? '')}</code>?
          </span>
        }
        busy={busy}
        onConfirm={() => void onDeleteConfirm()}
        onCancel={() => setConfirmDelete(null)}
      />
    </>
  );
}
